import { FormControl, FormHelperText, InputLabel, MenuItem, OutlinedInput, Select, SelectChangeEvent, styled } from '@mui/material'
import { useForkRef } from '@mui/material/utils'
import { CaretDownIcon } from '@phosphor-icons/react'
import { tokens } from 'ComponentLibrary/style-dictionary/output'
import { FocusEventHandler, ForwardedRef, ReactElement, ReactNode, RefAttributes, forwardRef, useId, useRef, useState } from 'react'
import { Control, FieldPath, FieldValues, UseControllerProps, useController } from 'react-hook-form'
import { isRequiredRule } from '../TextInput/TextInput'

export type DropdownOption<Value> = {
    label: string
    value: Value
    disabled?: boolean
}

export type DropdownValue<Value> = Value | undefined

export type DropdownProps<Value> = {
    'aria-label'?: string
    'aria-labelledby'?: string
    autoFocus?: boolean
    className?: string
    defaultValue?: DropdownValue<Value>
    disabled?: boolean
    disablePortal?: boolean
    errorMessage?: string
    fullWidth?: boolean
    initialFocus?: 'first' | 'selected'
    isValueEqual?: (optionValue: Value, selectedValue: Value) => boolean
    label?: string
    name?: string
    onBlur?: FocusEventHandler<HTMLInputElement>
    onValueChange?: (value: DropdownValue<Value>) => void
    openOnFocus?: boolean
    options: DropdownOption<Value>[]
    placeholder?: string
    readOnly?: boolean
    renderOption?: (option: DropdownOption<Value>) => ReactNode
    renderValue?: (value: Value, option: DropdownOption<Value>) => ReactNode
    required?: boolean
    value?: DropdownValue<Value>
}

function DropdownComponent<Value>(props: DropdownProps<Value>, ref: ForwardedRef<HTMLInputElement>) {
    const {
        'aria-label': ariaLabel,
        'aria-labelledby': ariaLabelledBy,
        autoFocus,
        className,
        defaultValue,
        disabled,
        disablePortal,
        errorMessage,
        fullWidth = true,
        initialFocus = 'selected',
        isValueEqual = Object.is,
        label,
        name,
        onBlur,
        onValueChange,
        openOnFocus = false,
        options,
        placeholder,
        readOnly,
        renderOption,
        renderValue,
        required = false,
        value,
    } = props
    const [open, setOpen] = useState(false)
    const dropdownRef = useRef<HTMLInputElement>(null)
    const inputRef = useForkRef(ref, dropdownRef)
    const restoringFocus = useRef(false)
    const closeMenu = () => {
        setOpen(false)
        if (openOnFocus) {
            restoringFocus.current = true
            dropdownRef.current?.focus()
        }
    }
    const inputId = useId()
    const labelId = useId()
    const isControlled = Object.prototype.hasOwnProperty.call(props, 'value')
    const resolveOptionIndex = (selectedValue: DropdownValue<Value>) => {
        if (selectedValue === undefined) {
            return ''
        }

        const optionIndex = options.findIndex((option) => isValueEqual(option.value, selectedValue))
        return optionIndex === -1 ? '' : `${optionIndex}`
    }
    const selectedIndex = isControlled ? resolveOptionIndex(value) : undefined
    const defaultSelectedIndex = defaultValue === undefined ? undefined : resolveOptionIndex(defaultValue)

    const handleChange = (event: SelectChangeEvent<string>) => {
        const option = options[Number(event.target.value)]
        onValueChange?.(option?.value)
    }

    return (
        <DropdownRoot
            className={className}
            disabled={disabled}
            error={errorMessage !== undefined}
            fullWidth={fullWidth}
            required={required}
        >
            {label ? <InputLabel id={labelId}>{label}</InputLabel> : undefined}
            <Select
                autoFocus={autoFocus}
                defaultValue={defaultSelectedIndex}
                displayEmpty={!label}
                IconComponent={DropdownIcon}
                id={inputId}
                input={<OutlinedInput label={label} />}
                inputProps={ariaLabel ? { 'aria-label': ariaLabel } : undefined}
                inputRef={inputRef}
                labelId={ariaLabelledBy ?? (label ? labelId : undefined)}
                MenuProps={{
                    variant: initialFocus === 'first' ? 'menu' : 'selectedMenu',
                    disablePortal,
                    disableEnforceFocus: openOnFocus,
                    disableRestoreFocus: openOnFocus,
                    onKeyDownCapture: (event) => {
                        if (openOnFocus && event.key === 'Tab') {
                            // Return focus before the browser moves to the next or previous cell.
                            event.stopPropagation()
                            closeMenu()
                        }
                    },
                    disableScrollLock: disablePortal,
                    slotProps: {
                        root: disablePortal
                            ? ({ anchorEl }) => ({
                                  container: () => {
                                      const resolvedAnchor = typeof anchorEl === 'function' ? anchorEl() : anchorEl
                                      return resolvedAnchor instanceof HTMLElement ? resolvedAnchor : null
                                  },
                              })
                            : undefined,
                        paper: {
                            sx: dropdownMenuStyles,
                        },
                    },
                }}
                name={name}
                onBlur={onBlur}
                onChange={handleChange}
                onClose={closeMenu}
                onFocus={() => {
                    if (openOnFocus && !restoringFocus.current) {
                        setOpen(true)
                    }
                    restoringFocus.current = false
                }}
                onOpen={() => setOpen(true)}
                open={open}
                readOnly={readOnly}
                renderValue={(index) => {
                    if (index === '') {
                        return label ? undefined : <Placeholder>{placeholder}</Placeholder>
                    }

                    const option = options[Number(index)]
                    return option ? (renderValue?.(option.value, option) ?? option.label) : undefined
                }}
                value={selectedIndex}
            >
                {options.map((option, index) => (
                    <MenuItem
                        disabled={option.disabled}
                        key={index}
                        value={`${index}`}
                        aria-label={renderOption ? option.label : undefined}
                    >
                        {renderOption?.(option) ?? option.label}
                    </MenuItem>
                ))}
            </Select>
            {errorMessage !== undefined ? <FormHelperText>{errorMessage}</FormHelperText> : undefined}
        </DropdownRoot>
    )
}

export const Dropdown = forwardRef(DropdownComponent) as <Value>(
    props: DropdownProps<Value> & RefAttributes<HTMLInputElement>
) => ReactElement

type DropdownFieldPath<TFieldValues extends FieldValues> = FieldPath<TFieldValues>

type FormDropdownRules<TFieldValues extends FieldValues, TName extends DropdownFieldPath<TFieldValues>> = NonNullable<
    UseControllerProps<TFieldValues, TName>['rules']
>

export type FormDropdownProps<
    TFieldValues extends FieldValues,
    Value,
    TName extends DropdownFieldPath<TFieldValues> = DropdownFieldPath<TFieldValues>,
> = Omit<DropdownProps<Value>, 'defaultValue' | 'errorMessage' | 'name' | 'required' | 'value'> & {
    control: Control<TFieldValues>
    defaultValue?: UseControllerProps<TFieldValues, TName>['defaultValue']
    name: TName
    rules?: FormDropdownRules<TFieldValues, TName>
}

export function FormDropdown<
    TFieldValues extends FieldValues,
    Value,
    TName extends DropdownFieldPath<TFieldValues> = DropdownFieldPath<TFieldValues>,
>({ control, defaultValue, name, onBlur, onValueChange, rules, ...dropdownProps }: FormDropdownProps<TFieldValues, Value, TName>) {
    const { field, fieldState } = useController({
        control,
        defaultValue,
        name,
        rules,
    })

    return (
        <Dropdown
            {...dropdownProps}
            errorMessage={fieldState.error ? (fieldState.error.message?.toString() ?? '') : undefined}
            name={field.name}
            onBlur={(event) => {
                field.onBlur()
                onBlur?.(event)
            }}
            onValueChange={(newValue) => {
                field.onChange(newValue)
                onValueChange?.(newValue)
            }}
            ref={field.ref}
            required={isRequiredRule(rules?.required)}
            value={field.value}
        />
    )
}

function DropdownIcon(props: { className?: string }) {
    return <CaretDownIcon {...props} aria-hidden size={tokens.SizingIconS} weight="bold" />
}

const Placeholder = styled('span')({
    color: tokens.ColorTextSubtle,
})

const DropdownRoot = styled(FormControl)({
    minHeight: '46px',
    minWidth: 0,
    position: 'relative',
    width: '100%',
    '& .MuiInputLabel-root': {
        ...tokens.TextS,
        color: tokens.ColorTextSubtle,
        maxWidth: 'calc(100% - 48px)',
        transform: `translate(${tokens.SizingSpacing150}, 14px) scale(1)`,
        zIndex: 1,
        '&.Mui-focused': {
            color: tokens.ColorTextSubtle,
        },
        '&.Mui-error': {
            color: tokens.ColorTextDangerSubtle,
        },
        '&.Mui-disabled': {
            color: tokens.ColorTextDisabled,
        },
        '&.MuiInputLabel-shrink': {
            ...tokens.TextXs,
            backgroundColor: tokens.ColorSurfaceDefault,
            maxWidth: 'calc(100% - 24px)',
            padding: `0 ${tokens.SizingSpacing075}`,
            transform: `translate(${tokens.SizingSpacing075}, -7px) scale(1)`,
        },
        '&.MuiInputLabel-shrink.Mui-disabled': {
            backgroundColor: 'transparent',
        },
    },
    '&:has(.MuiOutlinedInput-root:hover, .MuiInputLabel-shrink:hover)': {
        '& .MuiInputLabel-shrink:not(.Mui-focused):not(.Mui-disabled)': {
            backgroundColor: 'transparent',
        },
        '& .MuiOutlinedInput-root:not(.Mui-focused):not(.Mui-disabled)': {
            backgroundColor: tokens.ColorSurfaceHover,
        },
        '& .MuiOutlinedInput-root:not(.Mui-focused):not(.Mui-disabled):not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
            borderColor: tokens.ColorBorderDefault,
        },
    },
    '& .MuiOutlinedInput-root': {
        ...tokens.TextS,
        backgroundColor: tokens.ColorSurfaceDefault,
        borderRadius: tokens.SizingCornerS,
        color: tokens.ColorTextBold,
        height: '46px',
        padding: `0 ${tokens.SizingSpacing150}`,
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: tokens.ColorBorderDefault,
            borderWidth: tokens.SizingBorderS,
        },
        '&.Mui-focused': {
            backgroundColor: tokens.ColorSurfaceDefault,
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: tokens.ColorBorderFocused,
            borderWidth: tokens.SizingBorderS,
        },
        '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: tokens.ColorBorderDanger,
            borderWidth: tokens.SizingBorderS,
        },
        '&.Mui-disabled': {
            backgroundColor: tokens.ColorSurfaceDisabled,
            color: tokens.ColorTextDisabled,
        },
        '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
            borderColor: `${tokens.ColorBorderDisabled}!important`,
            borderWidth: tokens.SizingBorderS,
        },
    },
    '&:has(.MuiInputLabel-shrink) .MuiOutlinedInput-notchedOutline legend': {
        fontSize: tokens.TextXs.fontSize,
        maxWidth: `calc(100% - ${tokens.SizingSpacing100} - ${tokens.SizingBorderS})`,
    },
    '& .MuiSelect-select': {
        ...tokens.TextS,
        alignItems: 'center',
        boxSizing: 'border-box',
        color: tokens.ColorTextBold,
        display: 'flex',
        height: '100%!important',
        minHeight: '0!important',
        minWidth: 0,
        overflow: 'hidden',
        padding: `0 ${tokens.SizingSpacing300} 0 0!important`,
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        '&.Mui-disabled': {
            color: tokens.ColorTextDisabled,
            WebkitTextFillColor: tokens.ColorTextDisabled,
        },
    },
    '& .MuiSelect-icon': {
        color: tokens.ColorIconSubtle,
        right: tokens.SizingSpacing150,
    },
    '& .MuiSelect-icon.Mui-disabled': {
        color: tokens.ColorIconDisabled,
    },
    '& .MuiFormHelperText-root': {
        ...tokens.TextXs,
        boxSizing: 'border-box',
        color: tokens.ColorTextDangerSubtle,
        height: tokens.FontLineheightXs,
        margin: `${tokens.SizingSpacing050} 0 0`,
        overflow: 'hidden',
        padding: `0 ${tokens.SizingSpacing150}`,
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        width: '100%',
    },
})

const dropdownMenuStyles = {
    ...tokens.Flyout,
    backgroundColor: tokens.ColorSurfaceDefault,
    borderRadius: tokens.SizingCornerS,
    marginTop: tokens.SizingSpacing050,
    // Keep an odd number of rows visible so MUI can center the selected item without clipping the boundary rows.
    maxHeight: `min(calc(${tokens.SizingHeightWidgetDouble} - ${tokens.SizingSpacing400}), calc(100% - ${tokens.SizingSpacing1200}))`,
    '&& .MuiMenu-list': {
        boxSizing: 'border-box',
        padding: `${tokens.SizingSpacing050} 0`,
    },
    '&& .MuiMenuItem-root': {
        ...tokens.TextS,
        alignItems: 'center',
        backgroundColor: tokens.ColorSurfaceDefault,
        boxSizing: 'border-box',
        color: tokens.ColorTextBold,
        display: 'flex',
        gap: tokens.SizingSpacing100,
        height: tokens.SizingSpacing400,
        minHeight: tokens.SizingSpacing400,
        overflow: 'hidden',
        padding: `0 ${tokens.SizingSpacing150}`,
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        '&.Mui-focusVisible, &:hover': {
            backgroundColor: tokens.ColorSurfaceHover,
        },
        '&:active': {
            backgroundColor: tokens.ColorSurfacePressed,
        },
        '&.Mui-selected': {
            backgroundColor: tokens.ColorSurfaceSelectedDefault,
        },
        '&.Mui-selected.Mui-focusVisible, &.Mui-selected:hover': {
            backgroundColor: tokens.ColorSurfaceSelectedHover,
        },
        '&.Mui-selected:active': {
            backgroundColor: tokens.ColorSurfaceSelectedPressed,
        },
        '&.Mui-disabled': {
            color: tokens.ColorTextDisabled,
            opacity: 1,
        },
    },
}
